import { isEffective, } from "./concierge-assignment.js";
export const routingSkipReasons = Object.freeze([
    "ASSIGNEE_INELIGIBLE",
    "ASSIGNEE_UNAVAILABLE",
]);
// Time-boxed cover a supervisor set because the primary is away outranks the primary while it is
// effective; then the primary; then configured backup.
const RANK = ["TEMPORARY", "PRIMARY", "BACKUP"];
function skipReason(candidate) {
    if (!candidate.assigneeEligible)
        return "ASSIGNEE_INELIGIBLE";
    if (!candidate.assigneeAvailable)
        return "ASSIGNEE_UNAVAILABLE";
    return null;
}
/**
 * Where a handoff or a request goes: effective TEMPORARY, then effective PRIMARY, then effective
 * BACKUP (an assignment counts only when it is effective at `now` and its concierge is eligible and
 * available), then the supervisor queue (`supervisorAvailable`: at least one active, available
 * supervisor), then the explicit degraded UNASSIGNED state. Availability is never inferred from
 * presence, and the function never changes the primary relationship: it only reads assignments.
 * The result lists the effective assignments it passed over, so the decision is auditable.
 */
export function routeConciergeTarget(input) {
    const inForce = input.assignments.filter((assignment) => isEffective(assignment, input.now));
    const skipped = [];
    for (const kind of RANK) {
        for (const assignment of inForce.filter((row) => row.type === kind)) {
            const reason = skipReason(assignment);
            if (reason === null) {
                return Object.freeze({
                    kind,
                    staffUserId: assignment.conciergeStaffUserId,
                    assignmentId: assignment.id,
                    fallback: kind === "BACKUP",
                    degraded: false,
                    skipped: Object.freeze(skipped),
                });
            }
            skipped.push(Object.freeze({ kind, assignmentId: assignment.id, reason }));
        }
    }
    const evidence = Object.freeze(skipped);
    return input.supervisorAvailable
        ? Object.freeze({
            kind: "SUPERVISOR_QUEUE",
            fallback: true,
            degraded: false,
            skipped: evidence,
        })
        : Object.freeze({ kind: "UNASSIGNED", fallback: true, degraded: true, skipped: evidence });
}
/**
 * The coverage a WAITING handoff moves to when its current coverage cannot take it (CHT-734). It is the same
 * ranking as `routeConciergeTarget`, started AFTER the kind the handoff has now, so an escalation only ever
 * goes forward and never revisits a coverage the handoff already left:
 *
 * - `TEMPORARY` goes on to `PRIMARY`, then `BACKUP`, then the supervisor queue;
 * - `PRIMARY` goes on to `BACKUP`, then the supervisor queue;
 * - `BACKUP` goes on to the supervisor queue;
 * - `DIRECT` (a supervisor's choice) and `UNASSIGNED` start the ranking again from the top, because no
 *   coverage was ever tried for them;
 * - `SUPERVISOR_QUEUE` has nothing after it: `null`.
 *
 * An assignment counts only when it is effective at `now` and its concierge is eligible and available, and the
 * person who holds the handoff now is never chosen again. When every coverage fails the answer is the explicit
 * `UNASSIGNED` degraded state (an operational exception that supervisors must see), unless the handoff is
 * already `UNASSIGNED`: a move to the same state would change nothing, so the answer is `null`. The function
 * only reads assignments: the primary relationship never changes.
 *
 * `timeBased` is the ranking for "remains unaccepted" (`NOT_ACCEPTED_IN_TIME`): nobody is unavailable, somebody
 * simply did not accept. A wait alone never makes a handoff an operational exception, so this ranking NEVER answers
 * `UNASSIGNED` (the answer is `null`: nowhere to go), and it never chooses a person the handoff already left
 * (`visitedStaffUserIds`, read from the history of the handoff). Otherwise a handoff held by an available person
 * would be pushed into the exception list and bounce between the exception and the same person.
 */
export function escalateConciergeTarget(input) {
    if (input.currentKind === "SUPERVISOR_QUEUE")
        return null;
    const timeBased = input.timeBased === true;
    const visited = new Set(timeBased ? (input.visitedStaffUserIds ?? []) : []);
    const rankIndex = RANK.indexOf(input.currentKind);
    const ranks = rankIndex === -1 ? RANK : RANK.slice(rankIndex + 1);
    const inForce = input.assignments.filter((assignment) => isEffective(assignment, input.now));
    const skipped = [];
    for (const kind of ranks) {
        for (const assignment of inForce.filter((row) => row.type === kind)) {
            if (assignment.conciergeStaffUserId === input.currentStaffUserId)
                continue;
            if (visited.has(assignment.conciergeStaffUserId))
                continue;
            const reason = skipReason(assignment);
            if (reason === null) {
                return Object.freeze({
                    kind,
                    staffUserId: assignment.conciergeStaffUserId,
                    assignmentId: assignment.id,
                    fallback: kind === "BACKUP",
                    degraded: false,
                    skipped: Object.freeze(skipped),
                });
            }
            skipped.push(Object.freeze({ kind, assignmentId: assignment.id, reason }));
        }
    }
    const evidence = Object.freeze(skipped);
    if (input.supervisorAvailable) {
        return Object.freeze({
            kind: "SUPERVISOR_QUEUE",
            fallback: true,
            degraded: false,
            skipped: evidence,
        });
    }
    if (input.currentKind === "UNASSIGNED" || timeBased)
        return null;
    return Object.freeze({ kind: "UNASSIGNED", fallback: true, degraded: true, skipped: evidence });
}
/** `PRIMARY:ASSIGNEE_UNAVAILABLE,BACKUP:ASSIGNEE_INELIGIBLE`, or `null` when nothing was passed over: ids and codes only, for the audit row. */
export function skippedEvidence(decision) {
    if (decision === null || decision === undefined || decision.skipped.length === 0)
        return null;
    return decision.skipped.map((skip) => `${skip.kind}:${skip.reason}`).join(",");
}
